import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import OtpInput from "./ui/OtpInput";
import ResendOtp from "./ui/ResendOtp";
import LoginForm from "./forms/LoginForm";
import useOtpTimer from "../hooks/useOtpTimer";
import { useRequestOtpMutation, useVerifyOtpMutation, useConsentMutation } from "../hooks/mutations/authMutations";
import { loginSchema } from "../utils/validationSchemas/loginSchema";
import { LoginMethod, LoginProps, OtpRequestData, OtpVerifyData } from "../types";
import { OTP_CONFIG } from "../constants";
import logoImage from '../../../assets/images/logo.svg';
import { CustomButton } from "components/Button";
import CloseIcon from '@mui/icons-material/Close';
import { getDigitsOnly } from "utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const maskPhoneNumber = (phoneNumber: string) => {
  const digitsOnly = getDigitsOnly(phoneNumber);
  return digitsOnly.length >= 5
    ? `${digitsOnly.slice(0, 3)}******${digitsOnly.slice(-2)}`
    : digitsOnly;
};

const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) {
    return email;
  }

  const visiblePrefix = localPart.slice(0, Math.min(2, localPart.length));
  return `${visiblePrefix}${'*'.repeat(Math.max(localPart.length - visiblePrefix.length, 3))}@${domain}`;
};

const getDeliveryText = (loginMethod: LoginMethod) => {
  if (loginMethod === 'email') {
    return {
      destinationLabel: 'email address',
      inboxLabel: 'email inbox',
    };
  }

  if (loginMethod === 'voice') {
    return {
      destinationLabel: 'phone number',
      inboxLabel: 'incoming phone call',
    };
  }

  return {
    destinationLabel: 'phone number',
    inboxLabel: 'SMS inbox',
  };
};

const Login: React.FC<LoginProps> = ({ setSignup, showOtp, setShowOtp, handleClose }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [otpValue, setOtpValue] = useState<string>('');
  const [loginMethod, setLoginMethod] = useState<LoginMethod>('email');
  const [isChecked, setIsChecked] = useState<boolean>(true);
  const [showConsent, setShowConsent] = useState<boolean>(false);
  const [consentLoading, setConsentLoading] = useState<boolean>(false);
  const latestConsentStateRef = useRef({
    loginMethod: 'email' as LoginMethod,
    normalizedIdentifier: '',
  });

  const { canResend, resendTimer, startTimer } = useOtpTimer(OTP_CONFIG.RESEND_TIMER);

  const requestOtpMutation = useRequestOtpMutation();
  const verifyOtpMutation = useVerifyOtpMutation();
  const { mutate: checkConsent } = useConsentMutation();

  const methods = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: { identifier: "", otp: "" },
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const watchedIdentifier = methods.watch('identifier');
  const normalizedIdentifier = loginMethod === 'email'
    ? (watchedIdentifier || '').trim()
    : getDigitsOnly(watchedIdentifier || '');

  useEffect(() => {
    latestConsentStateRef.current = {
      loginMethod,
      normalizedIdentifier,
    };
  }, [loginMethod, normalizedIdentifier]);

  useEffect(() => {
    if (loginMethod === 'email') {
      setShowConsent(false);
      setConsentLoading(false);
      setIsChecked(true);
      return;
    }

    if (normalizedIdentifier.length === 10) {
      const phoneNumberForConsent = normalizedIdentifier;
      setConsentLoading(true);
      checkConsent(phoneNumberForConsent, {
        onSuccess: (res) => {
          const latestState = latestConsentStateRef.current;
          if (
            latestState.loginMethod === 'email' ||
            latestState.normalizedIdentifier !== phoneNumberForConsent
          ) {
            return;
          }

          if (!res?.data?.is_authorized) {
            setShowConsent(true);
            setIsChecked(false);
          } else {
            setShowConsent(false);
            setIsChecked(true);
          }
        },
        onError: (error) => {
          console.error("Consent check failed:", error);
        },
        onSettled: () => {
          const latestState = latestConsentStateRef.current;
          if (
            latestState.loginMethod !== 'email' &&
            latestState.normalizedIdentifier === phoneNumberForConsent
          ) {
            setConsentLoading(false);
          }
        }
      });
      return;
    }

    setShowConsent(false);
    setConsentLoading(false);
    setIsChecked(false);
  }, [normalizedIdentifier, loginMethod, checkConsent]);

  const handleLoginMethodChange = (method: LoginMethod) => {
    setLoginMethod(method);
    setOtpValue('');
    setShowOtp(false);
    setShowConsent(false);
    setConsentLoading(false);
    setIsChecked(method === 'email');
    methods.setValue('identifier', '');
    methods.setValue('otp', '');
    methods.clearErrors();
  };

  const handleSendOtp = async () => {
    const identifier = loginMethod === 'email'
      ? (methods.getValues('identifier') || '').trim()
      : getDigitsOnly(methods.getValues('identifier') || '');

    if (loginMethod === 'email' && !EMAIL_REGEX.test(identifier)) {
      methods.setError('identifier', { type: 'manual', message: 'Enter a valid email address' });
      return;
    }

    if (loginMethod !== 'email' && identifier.length !== 10) {
      methods.setError('identifier', { type: 'manual', message: 'Phone number must be 10 digits' });
      return;
    }

    if (loginMethod !== 'email' && showConsent && !isChecked) {
      methods.setError('identifier', { type: 'manual', message: 'Consent is required to receive an OTP' });
      return;
    }

    methods.clearErrors('identifier');
    methods.setValue('identifier', identifier);

    const data: OtpRequestData = {
      identifier,
      ...(isChecked && showConsent && { authorize_otp: true }),
      ...(loginMethod === 'voice' && { call: true }),
    };

    requestOtpMutation.mutate(data, {
      onSuccess: (response) => {
        if (response.success) {
          setShowOtp(true);
          startTimer();
        }
      },
      // onError: (error) => {
      //   const authError = AuthErrorHandler.handleError(error, 'OTP Request');
      //   toast.error(authError.message);
      // }
    });
  };

  const handleVerifyOtp = async (data: OtpVerifyData) => {
    verifyOtpMutation.mutate(data);
  };

  useEffect(() => {
    methods.setValue('otp', otpValue);
  }, [otpValue, methods]);

  const currentIdentifier = methods.getValues('identifier') || '';
  const deliveryText = getDeliveryText(loginMethod);
  const maskedDestination = loginMethod === 'email'
    ? maskEmail(currentIdentifier)
    : maskPhoneNumber(currentIdentifier);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleVerifyOtp)}>
        <Box className="auth-form-container">
          <img src={logoImage} alt="logo" className="auth-form-logo d-block" />
          {showOtp && <CloseIcon fontSize="small" className="auth-form-close-icon" onClick={handleClose} />}
          <p className="divider" />
          <p className="auth-form-title">{showOtp ? "OTP Verification" : "Welcome to LottoReco"}</p>
          {showOtp ? (
            <p className="auth-form-text mb-32">
              A verification code has been sent to your {deliveryText.destinationLabel}:{" "}
              <span className="phone-number">
                {maskedDestination}. Please check your {deliveryText.inboxLabel}.
              </span>
            </p>
          ) : (
            <p className="auth-form-text mb-24">
              Select where to receive your OTP, then enter the matching email address or phone number.
            </p>
          )}

          {!showOtp ? (
            <LoginForm
              onSubmit={handleSendOtp}
              loading={requestOtpMutation.isPending}
              identifier={methods.watch('identifier')}
              loginMethod={loginMethod}
              onLoginMethodChange={handleLoginMethodChange}
              methods={methods}
              isChecked={isChecked}
              showConsent={showConsent}
              consentLoading={consentLoading}
              handleCheckboxChange={handleCheckboxChange}
            />
          ) : (
            <>
              <OtpInput
                value={otpValue}
                onChange={setOtpValue}
                inputRef={inputRef}
              />
              <ResendOtp
                canResend={canResend}
                resendTimer={resendTimer}
                onResend={handleSendOtp}
              />
              <CustomButton
                loading={verifyOtpMutation.isPending}
                type="submit"
                className="button-base button-compact auth-form-button"
                disabled={otpValue?.length !== OTP_CONFIG.LENGTH}
              >
                Login
              </CustomButton>
            </>
          )}

          {!showOtp && (
            <div>
              <p className="divider-19" />
              <p className="do-not-have-account">
                Don't have an account?{" "}
                <span className="signup-text" onClick={() => setSignup(true)}>Sign Up</span>
              </p>
            </div>
          )}
        </Box>
      </form>
    </FormProvider>
  );
};

export default Login;
